import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, Globe, Mail, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const lastUpdated = "January 20, 2026";

  const sections = [
    {
      id: 'introduction',
      icon: FileText,
      title: 'Introduction',
      content: `Welcome to Web3 Ledger. We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our decentralized wallet platform and related services.

By using Web3 Ledger, you agree to the collection and use of information in accordance with this policy. We encourage you to read this Privacy Policy carefully to understand our practices regarding your personal data.`
    },
    {
      id: 'information-collection',
      icon: Database,
      title: 'Information We Collect',
      content: `We collect minimal information necessary to provide our services:

**Information You Provide:**
• Wallet addresses when you connect to our platform
• Email address (optional, for notifications)
• Communication preferences
• Support inquiries and feedback

**Automatically Collected Information:**
• Device information (browser type, operating system)
• IP address (anonymized)
• Usage data and interaction patterns
• Transaction metadata (on public blockchains)

**What We DON'T Collect:**
• Private keys or seed phrases (these never leave your device)
• Personal identification documents
• Financial account information from traditional banks
• Social security numbers or government IDs`
    },
    {
      id: 'data-usage',
      icon: Eye,
      title: 'How We Use Your Information',
      content: `We use the collected information for the following purposes:

• **Service Delivery:** To provide, maintain, and improve our wallet services
• **Security:** To detect, prevent, and address technical issues and fraudulent activities
• **Communication:** To send important updates, security alerts, and service notifications
• **Analytics:** To understand how users interact with our platform (aggregated, anonymized data)
• **Compliance:** To comply with legal obligations and regulatory requirements
• **Support:** To respond to your inquiries and provide customer assistance

We do NOT use your information for:
• Selling to third parties
• Targeted advertising
• Creating marketing profiles
• Sharing with data brokers`
    },
    {
      id: 'data-security',
      icon: Lock,
      title: 'Data Security',
      content: `We implement industry-leading security measures to protect your information:

**Technical Safeguards:**
• End-to-end encryption for all data transmissions
• AES-256 encryption for stored data
• Regular security audits and penetration testing
• Multi-factor authentication options
• Cold storage for sensitive operations

**Operational Security:**
• Strict access controls for employees
• Regular security training for all team members
• Incident response procedures
• 24/7 monitoring for suspicious activities

**Decentralized Architecture:**
• Your private keys are stored locally on your device
• We cannot access your funds or execute transactions on your behalf
• Self-custody model ensures you maintain full control`
    },
    {
      id: 'data-sharing',
      icon: Globe,
      title: 'Information Sharing',
      content: `We are committed to keeping your data private. We may share information only in these limited circumstances:

**With Your Consent:** When you explicitly authorize us to share information

**Service Providers:** With trusted partners who assist in operating our platform, bound by strict confidentiality agreements

**Legal Requirements:** When required by law, court order, or government request

**Safety:** To protect the rights, property, or safety of Web3 Ledger, our users, or the public

**We Never:**
• Sell your personal information
• Share data for advertising purposes
• Provide information to data brokers
• Allow third-party tracking on our platform`
    },
    {
      id: 'your-rights',
      icon: Shield,
      title: 'Your Rights & Choices',
      content: `You have the following rights regarding your personal data:

**Access:** Request a copy of the personal data we hold about you

**Correction:** Request correction of inaccurate or incomplete data

**Deletion:** Request deletion of your personal data (subject to legal retention requirements)

**Portability:** Receive your data in a structured, machine-readable format

**Objection:** Object to certain processing of your personal data

**Withdrawal:** Withdraw consent at any time where processing is based on consent

**How to Exercise Your Rights:**
Contact our privacy team at privacy@web3ledger.com or through our in-app support. We will respond to your request within 30 days.`
    },
    {
      id: 'cookies',
      icon: Database,
      title: 'Cookies & Tracking',
      content: `We use minimal cookies and tracking technologies:

**Essential Cookies:** Required for the platform to function properly
• Session management
• Security tokens
• User preferences

**Analytics (Optional):** Anonymized usage data to improve our services
• Page views and navigation patterns
• Feature usage statistics
• Performance monitoring

**We Do NOT Use:**
• Third-party advertising cookies
• Social media tracking pixels
• Cross-site tracking
• Fingerprinting technologies

You can control cookie preferences through your browser settings. Disabling essential cookies may affect platform functionality.`
    },
    {
      id: 'contact',
      icon: Mail,
      title: 'Contact Us',
      content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**Email:** privacy@web3ledger.com

**Support:** Through our in-app help center

**Response Time:** We aim to respond to all inquiries within 48 hours

**Data Protection Officer:**
For GDPR-related inquiries, contact our DPO at dpo@web3ledger.com

We take your privacy seriously and are committed to addressing any concerns you may have.`
    }
  ];

  return (
    <div className="privacy-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="privacy-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <Shield size={14} />
              Your Privacy Matters
            </span>
            <h1>Privacy Policy</h1>
            <p>How we protect and handle your information</p>
            <span className="last-updated">Last Updated: {lastUpdated}</span>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="privacy-nav-section">
        <div className="container">
          <div className="privacy-nav">
            {sections.map((section) => (
              <a 
                key={section.id} 
                href={`#${section.id}`}
                className="privacy-nav-item"
              >
                <section.icon size={16} />
                <span>{section.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="privacy-content">
        <div className="container">
          <div className="privacy-grid">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                className="privacy-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="section-icon">
                  <section.icon size={24} />
                </div>
                <h2>{section.title}</h2>
                <div className="section-content">
                  {section.content.split('\n\n').map((paragraph, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ 
                      __html: paragraph
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n•/g, '<br/>•')
                        .replace(/\n/g, '<br/>')
                    }} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Summary Box */}
          <motion.div 
            className="privacy-summary"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="summary-icon">
              <Shield size={32} />
            </div>
            <h3>Our Commitment to You</h3>
            <p>
              At Web3 Ledger, your privacy is not just a policy—it's a core principle. 
              We believe in true decentralization where you maintain complete control 
              over your data and assets. Your private keys never leave your device, 
              and we cannot access your funds.
            </p>
            <div className="summary-points">
              <div className="summary-point">
                <Lock size={18} />
                <span>Zero access to private keys</span>
              </div>
              <div className="summary-point">
                <Eye size={18} />
                <span>No data selling</span>
              </div>
              <div className="summary-point">
                <Database size={18} />
                <span>Minimal data collection</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
